import React, { useEffect, useState } from 'react';
import FocusTrap from 'focus-trap-react';
import type { MutableRefObject } from 'react';
import type { SizeType } from '../../util/types';
import { ModalMaskStyled, ModalWrapperStyled } from './Modal.styled';
import { usePortal } from '../../hooks/usePortal';
import { MODAL_CONTENT, MODAL_MASK } from '../../util/data-id';
import type { ToggleModalProps } from './types';

interface Props extends ToggleModalProps {
  onClose(): void;
  size?: SizeType;
  alwaysOnTop?: boolean;
  disableMaskClick?: boolean;
  children: React.ReactNode
}

const Modal = ({
  size = 'md',
  open,
  onClose,
  toggle,
  alwaysOnTop = false,
  disableMaskClick = false,
  children,
}: Props): JSX.Element => {
  const [ active, setActive ] = useState(false);
  const mask = React.useRef() as MutableRefObject<HTMLDivElement>;

  useEffect(() => {
    // DOM html
    const html = document.getElementsByTagName('html')[0];
    // DOM body
    const body = document.body;

    // useRef
    const { current } = mask;

    // TRANSITIONS TO BE TRIGGERED
    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName === 'opacity') {
        setActive(open);
      }
    };

    // CLOSE WHEN ESC KEY IS PRESSED
    const onEscKey = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        toggle(false);
        onClose();
      }
    };

    // CLOSE WHEN CLICK ON MASK
    const onClickOnMask = (event: MouseEvent) => {
      if (!disableMaskClick && event.target === current) {
        toggle(false);
        onClose();
      }
    };

    // NEEDED IN ORDER TO TRIGGER ENDING TRANSITION
    if (open) {
      body.classList.add('overflow-hidden');
      html.classList.add('overflow-hidden');

      setTimeout(() => {
        setActive(open);
      }, 10);
    }

    // ADDS EVENT LISTENERS
    if (current) {
      current.addEventListener('transitionend', onTransitionEnd);
      current.addEventListener('click', onClickOnMask);
      window.addEventListener('keyup', onEscKey);
    }

    // REMOVES EVENT LISTENERS
    return () => {
      if (current) {
        current.removeEventListener('transitionend', onTransitionEnd);
        current.removeEventListener('click', onClickOnMask);
      }

      body.removeAttribute('class');
      html.removeAttribute('class');
      window.removeEventListener('keyup', onEscKey);
    };
  }, [ open, toggle ]);

  const portal = usePortal(
    <FocusTrap
      active={open || active}
      focusTrapOptions={{
        initialFocus: `[data-id="${MODAL_MASK}"]`,
        fallbackFocus: `[data-id="${MODAL_MASK}"]`,
      }}
    >
      <ModalMaskStyled
        ref={mask}
        tabIndex={0}
        data-id={MODAL_MASK}
        data-toggle={active && open && 'true'}
        $alwaysOnTop={alwaysOnTop}
      >
        <ModalWrapperStyled
          $size={size}
          data-id={MODAL_CONTENT}
        >
          <div>{children}</div>
        </ModalWrapperStyled>
      </ModalMaskStyled>
    </FocusTrap>,
    'portal-modal',
  );

  return (open || active) ? portal : <></>;
};

interface ModalFooterProps {
  children: React.ReactNode;
  justify?: 'between' | 'end';
}

const ModalFooter = ({ children, justify = 'between' }: ModalFooterProps):JSX.Element => (
  <div className={`container flex items-center pt-md border-t-2 border-header justify-${justify}`}>
    {children}
  </div>
);

Modal.displayName = 'Modal';

export { Modal as default, ModalFooter };
